import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowPathIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  createTPRMVendorApi,
  getTPRMVendorsApi,
} from "../../../services/axios/AuthApis/TPRMApis";
import DataTable from "../../common/DataTable";
import PageLoader from "../../ui/PageLoader";
import AssessmentInitiationModal from "./AssessmentInitiationModal";
import useTPRMDialog from "./useTPRMDialog";

const getVendorResults = (data) => {
  const results = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
      ? data.results
      : [];
  const count = Number.isFinite(Number(data?.count))
    ? Number(data.count)
    : results.length;
  return { results, count };
};

const STATUS_OPTIONS = [
  "Draft",
  "Active",
  "Inactive",
  "Under Review",
  "Onboarding",
  "Pending Assessment",
  "Pending Approval",
  "Suspended",
  "Offboarding",
  "Offboarded",
  "Rejected",
];

const RISK_OPTIONS = ["Unassessed", "Low", "Medium", "High", "Critical"];

const INDUSTRY_OPTIONS = [
  { value: "technology", label: "Technology" },
  { value: "financial_services", label: "Financial Services" },
  { value: "healthcare", label: "Healthcare" },
  { value: "manufacturing", label: "Manufacturing" },
  { value: "retail", label: "Retail" },
  { value: "energy", label: "Energy" },
  { value: "government", label: "Government" },
  { value: "education", label: "Education" },
  { value: "consulting", label: "Consulting" },
  { value: "other", label: "Other" },
];

const inputClassName =
  "h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 shadow-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100";

const emptyVendorForm = {
  name: "",
  industry: "",
  website: "",
  primaryContactName: "",
  primaryContactEmail: "",
};

const getErrorMessage = (value) => {
  if (Array.isArray(value)) return value.join(" ");
  return typeof value === "string" ? value : "";
};

const isValidWebsite = (value) => {
  try {
    const website = new URL(value);
    return ["http:", "https:"].includes(website.protocol);
  } catch {
    return false;
  }
};

const VendorsPage = () => {
  const [vendors, setVendors] = useState([]);
  const [totalVendors, setTotalVendors] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [reloadKey, setReloadKey] = useState(0);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);
  const [createErrors, setCreateErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [assessmentVendor, setAssessmentVendor] = useState(null);
  const createInProgress = useRef(false);

  const pageCount = Math.ceil(totalVendors / pagination.pageSize);
  const filterValues = useMemo(
    () => Object.fromEntries(columnFilters.map(({ id, value }) => [id, value])),
    [columnFilters],
  );

  useEffect(() => {
    let cancelled = false;
    const loadVendors = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await getTPRMVendorsApi({
          ...filterValues,
          limit: pagination.pageSize,
          offset: pagination.pageIndex * pagination.pageSize,
        });
        const result = getVendorResults(data);
        if (!cancelled) {
          setVendors(result.results);
          setTotalVendors(result.count);
          setHasLoaded(true);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(
            requestError.response?.data?.detail ||
              requestError.message ||
              "Unable to load vendors.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadVendors();
    return () => {
      cancelled = true;
    };
  }, [filterValues, pagination.pageIndex, pagination.pageSize, reloadKey]);

  const columns = useMemo(
    () => [
      {
        id: "search",
        header: "Vendor",
        accessorFn: (vendor) => vendor.name || "Unnamed vendor",
        cell: ({ row }) => (
          <div>
            <div className="font-medium text-slate-900 dark:text-white">
              {row.original.name || "Unnamed vendor"}
            </div>
            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              {row.original.vendor_id}
            </div>
          </div>
        ),
        size: 260,
        meta: {
          filter: {
            variant: "text",
            label: "Vendor name",
            placeholder: "Search vendor name",
          },
        },
      },
      {
        accessorKey: "industry",
        header: "Industry",
        cell: ({ getValue }) => getValue() || "—",
      },
      {
        id: "risk_level",
        accessorFn: (vendor) => vendor.risk_tier || "Unassessed",
        header: "Risk level",
        cell: ({ getValue }) => getValue() || "Unassessed",
        meta: {
          filter: {
            variant: "select",
            label: "Risk level",
            options: RISK_OPTIONS.map((value) => ({
              value,
              label: value,
            })),
          },
        },
      },
      {
        accessorKey: "risk_score",
        header: "Risk score",
        cell: ({ getValue }) => {
          const value = getValue();
          return value == null ? "—" : value;
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ getValue }) => getValue() || "—",
        meta: {
          filter: {
            variant: "select",
            label: "Lifecycle status",
            options: STATUS_OPTIONS.map((value) => ({ value, label: value })),
          },
        },
      },
      {
        id: "assessment",
        header: "Assessment",
        enableSorting: false,
        cell: ({ row }) => (
          <button
            className="whitespace-nowrap rounded-lg border border-blue-200 bg-blue-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-blue-900 dark:focus:ring-offset-slate-900"
            onClick={() => setAssessmentVendor(row.original)}
            title="Initiate assessment"
            type="button"
          >
            Initiate assessment
          </button>
        ),
      },
    ],
    [],
  );

  const handlePaginationChange = useCallback((updater) => {
    setPagination((current) =>
      typeof updater === "function" ? updater(current) : updater,
    );
  }, []);

  const closeCreateForm = () => {
    if (creating) return;
    setShowCreateForm(false);
    setVendorForm(emptyVendorForm);
    setCreateErrors({});
  };
  const createDialogRef = useTPRMDialog({
    onClose: closeCreateForm,
    closeOnEscape: !creating,
    enabled: showCreateForm,
  });

  const submitCreateVendor = async (event) => {
    event.preventDefault();
    if (createInProgress.current) return;

    const values = {
      name: vendorForm.name.trim(),
      industry: vendorForm.industry,
      website: vendorForm.website.trim(),
      primary_contact: {
        name: vendorForm.primaryContactName.trim(),
        email: vendorForm.primaryContactEmail.trim(),
      },
    };
    const validationErrors = {};
    if (!values.name) validationErrors.name = "Vendor name is required.";
    if (!values.industry) validationErrors.industry = "Select an industry.";
    if (!values.website || !isValidWebsite(values.website)) {
      validationErrors.website =
        "Enter a valid website URL starting with http:// or https://.";
    }
    if (!values.primary_contact.name) {
      validationErrors.primaryContactName = "Primary contact name is required.";
    }
    if (!values.primary_contact.email) {
      validationErrors.primaryContactEmail = "Primary contact email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.primary_contact.email)) {
      validationErrors.primaryContactEmail = "Enter a valid email address.";
    }
    if (Object.keys(validationErrors).length) {
      setCreateErrors(validationErrors);
      return;
    }

    createInProgress.current = true;
    setCreating(true);
    setCreateErrors({});
    setSuccessMessage("");
    try {
      await createTPRMVendorApi(values);
      setSuccessMessage(`${values.name} was added successfully.`);
      setShowCreateForm(false);
      setVendorForm(emptyVendorForm);
      setColumnFilters([]);
      setPagination((current) => ({ ...current, pageIndex: 0 }));
      setReloadKey((current) => current + 1);
    } catch (requestError) {
      const responseData = requestError.response?.data || {};
      const fieldErrors = {};
      ["name", "industry", "website"].forEach((field) => {
        const message = getErrorMessage(responseData[field]);
        if (message) fieldErrors[field] = message;
      });
      const contactErrors = responseData.primary_contact || {};
      ["name", "email"].forEach((field) => {
        const message = getErrorMessage(contactErrors[field]);
        if (message) fieldErrors[`primaryContact${field === "name" ? "Name" : "Email"}`] = message;
      });
      const detail =
        getErrorMessage(responseData.detail) ||
        requestError.message ||
        "Unable to create the vendor.";
      setCreateErrors({ ...fieldErrors, form: detail });
    } finally {
      createInProgress.current = false;
      setCreating(false);
    }
  };

  const createVendorDialog = showCreateForm ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) closeCreateForm();
      }}
    >
      <section
        aria-labelledby="add-vendor-title"
        aria-modal="true"
        className="relative my-auto w-full max-w-xl rounded-xl border border-slate-200 bg-white p-5 shadow-xl dark:border-slate-700 dark:bg-slate-900 sm:p-6"
        ref={createDialogRef}
        role="dialog"
        tabIndex={-1}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2
              className="text-lg font-semibold text-slate-900 dark:text-white"
              id="add-vendor-title"
            >
              Add vendor
            </h2>
            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              Enter the vendor’s basic details to add it to your register.
            </p>
          </div>
          <button
            aria-label="Close add vendor form"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-50 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            disabled={creating}
            onClick={closeCreateForm}
            title="Close add vendor form"
            type="button"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <form className="space-y-4" noValidate onSubmit={submitCreateVendor}>
          {createErrors.form && (
            <p
              className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300"
              role="alert"
            >
              {createErrors.form}
            </p>
          )}
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Vendor name <span aria-hidden="true">*</span>
            <input
              aria-label="Vendor name"
              aria-invalid={Boolean(createErrors.name)}
              aria-describedby={createErrors.name ? "vendor-name-error" : undefined}
              autoComplete="organization"
              className={`${inputClassName} mt-1`}
              maxLength={200}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              required
              type="text"
              value={vendorForm.name}
            />
            {createErrors.name && (
              <span
                className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                id="vendor-name-error"
              >
                {createErrors.name}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Industry <span aria-hidden="true">*</span>
            <select
              aria-label="Industry"
              aria-invalid={Boolean(createErrors.industry)}
              aria-describedby={
                createErrors.industry ? "vendor-industry-error" : undefined
              }
              className={`${inputClassName} mt-1`}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  industry: event.target.value,
                }))
              }
              required
              value={vendorForm.industry}
            >
              <option value="">Select an industry</option>
              {INDUSTRY_OPTIONS.map((industry) => (
                <option key={industry.value} value={industry.value}>
                  {industry.label}
                </option>
              ))}
            </select>
            {createErrors.industry && (
              <span
                className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                id="vendor-industry-error"
              >
                {createErrors.industry}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Vendor website / domain <span aria-hidden="true">*</span>
            <input
              aria-label="Vendor website"
              aria-invalid={Boolean(createErrors.website)}
              aria-describedby={
                createErrors.website ? "vendor-website-error" : undefined
              }
              autoComplete="url"
              className={`${inputClassName} mt-1`}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  website: event.target.value,
                }))
              }
              placeholder="https://example.com"
              required
              type="url"
              value={vendorForm.website}
            />
            {createErrors.website && (
              <span
                className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                id="vendor-website-error"
              >
                {createErrors.website}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Primary contact name <span aria-hidden="true">*</span>
            <input
              aria-label="Primary contact name"
              aria-invalid={Boolean(createErrors.primaryContactName)}
              aria-describedby={
                createErrors.primaryContactName
                  ? "vendor-contact-name-error"
                  : undefined
              }
              autoComplete="name"
              className={`${inputClassName} mt-1`}
              maxLength={100}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  primaryContactName: event.target.value,
                }))
              }
              required
              type="text"
              value={vendorForm.primaryContactName}
            />
            {createErrors.primaryContactName && (
              <span
                className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                id="vendor-contact-name-error"
              >
                {createErrors.primaryContactName}
              </span>
            )}
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
            Primary contact email <span aria-hidden="true">*</span>
            <input
              aria-label="Primary contact email"
              aria-invalid={Boolean(createErrors.primaryContactEmail)}
              aria-describedby={
                createErrors.primaryContactEmail
                  ? "vendor-contact-email-error"
                  : undefined
              }
              autoComplete="email"
              className={`${inputClassName} mt-1`}
              maxLength={254}
              onChange={(event) =>
                setVendorForm((current) => ({
                  ...current,
                  primaryContactEmail: event.target.value,
                }))
              }
              required
              type="email"
              value={vendorForm.primaryContactEmail}
            />
            {createErrors.primaryContactEmail && (
              <span
                className="mt-1 block text-xs text-rose-600 dark:text-rose-400"
                id="vendor-contact-email-error"
              >
                {createErrors.primaryContactEmail}
              </span>
            )}
          </label>
          <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row sm:justify-end">
            <button
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              disabled={creating}
              onClick={closeCreateForm}
              type="button"
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
              disabled={creating}
              type="submit"
            >
              {creating ? "Adding vendor..." : "Add vendor"}
            </button>
          </div>
        </form>
      </section>
    </div>
  ) : null;

  const addVendorButton = (
    <button
      className="inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-medium text-white transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950"
      onClick={() => {
        setSuccessMessage("");
        setCreateErrors({});
        setShowCreateForm(true);
      }}
      type="button"
    >
      <PlusIcon className="h-4 w-4" />
      Add vendor
    </button>
  );

  if (loading) {
    if (!hasLoaded) {
      return <PageLoader title="Loading vendors..." minH="min-h-[40vh]" />;
    }
  }

  if (error) {
    return (
      <div className="space-y-4">
        {successMessage && (
          <p className="text-sm text-emerald-700 dark:text-emerald-300" role="status">
            {successMessage}
          </p>
        )}
        <div className="flex justify-end">{addVendorButton}</div>
        <section
          className="rounded-xl border border-rose-200 bg-white p-6 dark:border-rose-900 dark:bg-slate-900"
          role="alert"
        >
          <h2 className="font-semibold text-slate-900 dark:text-white">
            Vendors could not be loaded
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{error}</p>
          <button
            type="button"
            onClick={() => setReloadKey((current) => current + 1)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Try again
          </button>
        </section>
        {createVendorDialog}
        {assessmentVendor && (
          <AssessmentInitiationModal
            vendor={assessmentVendor}
            onClose={() => setAssessmentVendor(null)}
            onCreated={() => setReloadKey((current) => current + 1)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {successMessage && (
        <p className="text-sm text-emerald-700 dark:text-emerald-300" role="status">
          {successMessage}
        </p>
      )}
      <div className="flex justify-end">{addVendorButton}</div>
      <DataTable
        columns={columns}
        data={vendors}
        loading={loading}
        error={null}
        emptyMessage="No vendors match the current search and filters."
        enableSorting={false}
        enableGlobalFilter={false}
        enableColumnFilters
        enableColumnVisibility={false}
        manualPagination
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={(updater) => {
          const next = typeof updater === "function" ? updater(columnFilters) : updater;
          setColumnFilters(next);
          setPagination((current) => ({ ...current, pageIndex: 0 }));
        }}
        rowCount={totalVendors}
        pageCount={pageCount}
        pagination={pagination}
        onPaginationChange={handlePaginationChange}
        className="min-w-0 [&_.overflow-x-auto]:dark:border-slate-700 [&_.overflow-x-auto]:dark:bg-slate-900 [&_thead]:dark:bg-slate-800 [&_thead]:dark:text-slate-300 [&_th]:dark:border-slate-700 [&_tbody]:dark:divide-slate-800 [&_tbody_tr]:dark:hover:bg-slate-800 [&_tbody_td]:dark:text-slate-300 [&_td]:dark:border-slate-800 [&_tfoot]:dark:bg-slate-900 [&_tfoot]:dark:text-slate-300"
        getRowId={(vendor) => vendor.vendor_id}
      />
      {createVendorDialog}
      {assessmentVendor && (
        <AssessmentInitiationModal
          vendor={assessmentVendor}
          onClose={() => setAssessmentVendor(null)}
          onCreated={() => setReloadKey((current) => current + 1)}
        />
      )}
    </div>
  );
};

export default VendorsPage;
